import { Button, Modal } from 'antd'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { clone } from 'src/util/Util'
import './style.css'

interface Props {
    schedule: number[][]
    setSchedule: (newValue: number[][]) => void
    visible: boolean
    setVisible: (visible: boolean) => void
}

const ALL_HOURS = Array.from(Array(24).keys())
const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

export const ALWAYS_WORK = WEEKDAYS.map(weekday => clone(ALL_HOURS).fill(1))

export const clear = (schedule: number[][]) => {

    if (schedule.length !== ALWAYS_WORK.length) return false

    const empty = schedule.every((hours, weekday) => {
        return hours.every((checked, hour) => checked === ALWAYS_WORK[weekday][hour])
    })

    return empty ? undefined : schedule
}

export const renderDay = (day: number[]) => {

    if (!day || day.every(h => h == 1)) return <span>full day</span>

    const compressed = compressHours(day)
    return compressed.length > 0
        ? <span>{compressed.join(', ')}</span>
        : <span className="form-item-text">not selected</span>
}

const compressHours = (hours) => {
    const result: string[] = []

    let start = null
    hours.forEach((hour, index) => {
        if (hour === 1 && start === null) {
            start = index
        }

        if ((hour === 0 || index === hours.length - 1) && start !== null) {
            const end = hour === 1 ? index : index - 1
            result.push(`${start}h - ${end}h`)
            start = null
        }
    })

    return result
}

export const Scheduler = ({ schedule, setSchedule, visible, setVisible }: Props) => {

    const [dragValue, setDragValue] = useState<{ weekday: number, hour: number, checked: boolean } | undefined>(undefined)

    useEffect(() => {

        if (visible) {
            document.addEventListener('mouseup', handleMouseUp)
        } else {
            document.removeEventListener('mouseup', handleMouseUp)
        }

        schedule.forEach((hours, weekday) => hours.forEach((checked, hour) => setHour(weekday, hour, checked === 1)))
    }, [visible])

    const setWeekday = (weekday: number, checked: boolean) => {
        ALL_HOURS.forEach(hour => setHour(weekday, hour, checked))
    }

    const setHour = (weekday: number, hour: number, checked?: boolean) => {
        setHourElem(weekday, hour, checked)
        setWeekdayElem(weekday, isWeekdayAllHoursEnabled(weekday))
    }

    const isHourEnabled = (weekday: number, hour: number) => {
        const hourElem = document.getElementById(`cell_${weekday}_${hour}`)
        return hourElem && hourElem.classList.contains('active')
    }

    const setHourElem = (weekday: number, hour: number, checked?: boolean) => {
        const hourElem = document.getElementById(`cell_${weekday}_${hour}`)
        setClassActive(hourElem, checked)
    }

    const isWeekdayEnabled = (weekday: number) => {
        const hourElem = document.getElementById(`row_${weekday}`)
        return hourElem && hourElem.classList.contains('active')
    }

    const setWeekdayElem = (weekday: number, checked?: boolean) => {
        const weekdayElem = document.getElementById(`row_${weekday}`)
        setClassActive(weekdayElem, checked)
    }

    const setClassActive = (elem, checked: boolean | undefined) => {
        if (elem) {
            if (checked) {
                if (!elem.classList.contains('active')) {
                    elem.classList.add('active')
                }
            } else if (elem.classList.contains('active')) {
                elem.classList.remove('active')
            }
        }
    }

    const isWeekdayAllHoursEnabled = (weekday: number) => {
        return ALL_HOURS.find(value => !isHourEnabled(weekday, value)) === undefined
    }

    const handleMouseMove = (event) => {
        if (dragValue == undefined) return

        const weekdayAttr = event.target.getAttribute('data-weekday')
        const hourAttr = event.target.getAttribute('data-hour')

        if (weekdayAttr === null || hourAttr === null) return

        const weekday = parseInt(weekdayAttr, 10)
        const hour = parseInt(hourAttr, 10)

        const rowStart = Math.min(weekday, dragValue.weekday)
        const rowEnd = Math.max(weekday, dragValue.weekday)
        const colStart = Math.min(hour, dragValue.hour)
        const colEnd = Math.max(hour, dragValue.hour)

        for (let i = rowStart; i <= rowEnd; i++) {
            for (let j = colStart; j <= colEnd; j++) {
                setHour(i, j, dragValue.checked)
            }
        }
    }

    const handleMouseUp = () => {
        setDragValue(undefined)
    }

    const collect = () => {
        const result: number[][] = []
        WEEKDAYS.forEach(((weekdayStr, weekday) => {
            const resultHours: Array<number> = []
            ALL_HOURS.forEach(hour => resultHours.push(isHourEnabled(weekday, hour) ? 1 : 0))
            result.push(resultHours)
        }))
        return result
    }

    const renderFooter = () => <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.6rem' }}>
        {/* Legend */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{
                    width: '16px',
                    height: '16px',
                    backgroundColor: 'var(--ten-accent-light-2)',
                    border: '1px solid var(--ten-accent)',
                    borderRadius: '2px'
                }} />
                <span style={{ fontSize: '12px' }}>Set</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{
                    width: '16px',
                    height: '16px',
                    border: '1px solid var(--ten-accent)',
                    borderRadius: '2px'
                }} />
                <span style={{ fontSize: '12px' }}>Unset</span>
            </div>
        </div>

        {/* Buttons */}
        <div>
            <Button key="ok" type="primary" onClick={() => {
                setSchedule(collect())
                setVisible(false)
            }}
                style={{ width: 140, height: 36, fontSize: 12, borderRadius: 8 }}
            >
                Save
            </Button>
            <Button key="cancel" danger
                onClick={() => setVisible(false)}
                style={{ width: 140, height: 36, fontSize: 12, borderRadius: 8 }}
            >
                Cancel
            </Button>
        </div>
    </div>

    const renderDayParting = () => <table className="day-parting-grid" id="dayPartingTable">
        <thead>
            <tr style={{ fontSize: 7, height: 24 }}>
                <th />
                <th />
                {ALL_HOURS.map((hour) => (
                    <th key={hour}>{(hour < 10 ? '0' : '') + hour + ':00'}</th>
                ))}
                <th />
            </tr>
        </thead>
        <tbody>
            {WEEKDAYS.map((weekdayStr, weekday) => (
                <tr key={weekdayStr}>
                    <td style={{ paddingRight: '12px', textAlign: 'left', fontSize: 12 }}>{weekdayStr}</td>
                    <td style={{ paddingRight: '24px' }}>
                        <div
                            id={`row_${weekday}`}
                            className={`day-parting-cell ${isWeekdayEnabled(weekday) ? 'active' : ''} ${weekday === WEEKDAYS.length - 1 ? 'last-row' : ''} last-col first-col`}
                            onClick={() => setWeekday(weekday, !isWeekdayEnabled(weekday))}
                        />
                    </td>
                    {ALL_HOURS.map((hour, hIndex) => {
                        const isLastCol = hIndex === ALL_HOURS.length - 1
                        const isLastRow = weekday === WEEKDAYS.length - 1
                        const isFirstCol = hIndex === 0
                        return (
                            <td key={hour} className="day-parting-cell-parent">
                                <div
                                    id={`cell_${weekday}_${hour}`}
                                    data-weekday={weekday}
                                    data-hour={hour}
                                    className={`day-parting-cell ${isLastCol ? 'last-col' : ''} ${isFirstCol ? 'first-col' : ''} ${isLastRow ? 'last-row' : ''}`}
                                    onMouseDown={(e) => {
                                        e.preventDefault()

                                        const active = isHourEnabled(weekday, hour)
                                        setHour(weekday, hour, !active)
                                        setDragValue({ weekday, hour, checked: !active })
                                    }}
                                    onMouseMove={handleMouseMove}
                                />
                            </td>
                        )
                    })}
                </tr>
            ))}
        </tbody>
    </table>

    return <Modal
        centered
        width="auto"
        title="Set Days and Hours"
        open={visible}
        className="day-parting-modal"
        onCancel={() => setVisible(false)}
        footer={renderFooter()}
    >
        {renderDayParting()}
    </Modal>
}


// WEBPACK FOOTER //
// ./src/component/app/schedule/Scheduler.tsx